// createView — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'create-view' })` op (DSE2 §3.D2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D2 — View / Materialized View UI.
//
// Per-engine surface (per DSE2 dialect.view):
//   - mssql:   CREATE [OR ALTER] VIEW <name> [WITH SCHEMABINDING] AS <body>
//              `orReplace` rejects (use `mssqlOrAlter` instead).
//   - pgsql:   CREATE [OR REPLACE] VIEW <name> [WITH (option = …)] AS <body>
//   - mysql:   CREATE [OR REPLACE] [ALGORITHM = …] [DEFINER = …]
//              [SQL SECURITY {DEFINER|INVOKER}] VIEW <name>
//              AS <body> [WITH {LOCAL|CASCADED} CHECK OPTION]
//   - oracle:  CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE] [FORCE]
//              VIEW <name> AS <body>
//   - sqlite:  CREATE [TEMPORARY] VIEW <name> AS <body>
//              `orReplace` is unsupported (no atomic CREATE OR REPLACE).

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, ViewDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface CreateViewArgs {
  readonly view: ViewDef;
}

export function buildCreateViewEdit(args: CreateViewArgs): DdlEdit {
  return { op: 'create-view', view: args.view };
}

export async function commitCreateView(
  ctx: VqeDdlEditContext,
  args: CreateViewArgs,
): Promise<void> {
  const edit = buildCreateViewEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

/** Every supported engine accepts CREATE VIEW. The parameter is preserved
 *  for surface symmetry with sibling predicates (`isAlterViewSupported` etc.)
 *  so consumers can call them through the same shape. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isCreateViewSupported(_engine: SqlEngine): boolean {
  return true;
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths. Returns
 *  `null` when args are acceptable, else a user-facing message. */
export function validateCreateViewForEngine(
  engine: SqlEngine,
  args: CreateViewArgs,
): string | null {
  const view = args.view;
  if (view.bodyText.trim().length === 0) {
    return 'View body (SELECT) is required.';
  }
  if (view.name.name.trim().length === 0) {
    return 'View name is required.';
  }
  if (engine === 'mssql') {
    if (view.orReplace) {
      return (
        'MSSQL does not have CREATE OR REPLACE VIEW. Use OR ALTER instead '
        + '(toggle the "Replace existing" option — it maps to OR ALTER on MSSQL).'
      );
    }
  }
  if (engine !== 'mssql') {
    if (view.mssqlOrAlter) {
      return `${engine} does not accept OR ALTER. Use OR REPLACE instead.`;
    }
    if (view.mssqlWithSchemabinding) {
      return `${engine} does not support WITH SCHEMABINDING (MSSQL-only).`;
    }
  }
  if (engine !== 'postgres' && view.pgsqlOptions) {
    return `${engine} does not support pgsql WITH (security_barrier / check_option) options.`;
  }
  if (engine !== 'mysql') {
    if (view.mysqlAlgorithm) {
      return `${engine} does not support ALGORITHM = … (MySQL-only).`;
    }
    if (view.mysqlDefiner) {
      return `${engine} does not support DEFINER = … (MySQL-only).`;
    }
    if (view.mysqlSqlSecurity) {
      return `${engine} does not support SQL SECURITY {DEFINER|INVOKER} (MySQL-only).`;
    }
    if (view.mysqlCheckOption) {
      return `${engine} does not support WITH … CHECK OPTION (MySQL-only).`;
    }
  }
  if (engine !== 'oracle') {
    if (view.oracleEditionable) {
      return `${engine} does not support EDITIONABLE (Oracle-only).`;
    }
    if (view.oracleNonEditionable) {
      return `${engine} does not support NONEDITIONABLE (Oracle-only).`;
    }
    if (view.oracleForce) {
      return `${engine} does not support FORCE (Oracle-only).`;
    }
  }
  if (engine === 'oracle' && view.oracleEditionable && view.oracleNonEditionable) {
    return 'Oracle EDITIONABLE and NONEDITIONABLE are mutually exclusive.';
  }
  if (engine !== 'sqlite' && view.sqliteTemporary) {
    return `${engine} does not support TEMPORARY VIEW (SQLite-only).`;
  }
  if (engine === 'sqlite' && view.orReplace) {
    return (
      'SQLite has no atomic CREATE OR REPLACE VIEW. Drop and recreate the '
      + 'view in two steps: DROP VIEW IF EXISTS <name>, then CREATE VIEW.'
    );
  }
  return null;
}

/** Returns the engine-correct "in-place ALTER" flag set on a `ViewDef` so
 *  the consumer can ship body-edit popovers without learning per-engine
 *  flags. mssql → `mssqlOrAlter`; pgsql/mysql/oracle → `orReplace`;
 *  sqlite throws — sqlite has no atomic CREATE OR REPLACE. */
export function applyAlterFlagForEngine(
  engine: SqlEngine,
  view: ViewDef,
): ViewDef {
  if (engine === 'mssql') return { ...view, mssqlOrAlter: true };
  if (engine === 'sqlite') {
    throw new Error(
      'applyAlterFlagForEngine: sqlite has no atomic CREATE OR REPLACE — '
        + 'consumer must DROP + CREATE in two ops.',
    );
  }
  return { ...view, orReplace: true };
}
